import React from "react";
import styled from "styled-components";
import Icon from "components/ui/Icon";
import { ApiAdminUser } from "utils/api/admin/user";
import { ApiNotificationType } from "utils/api/notifications";
import { TAX_REFUND_ADVANCE_COMPLETED_STATES } from "utils/constants/onboarding";
import { ProductName } from "types/general";
import { OnboardingState } from "types/onboarding";
import OnboardingStatusTag from "../../../components/OnboardingStatusTag";

enum Progress {
  ready,
  inProgress,
  complete,
}

interface Props {
  user: ApiAdminUser;
  productName: ProductName;
}

const hasInProgressProduct = ({ user, productName }: Props) =>
  user.notifications.some(
    (notification) =>
      notification.type === ApiNotificationType.onboardingState &&
      notification.params.product === productName
  );

const getProgress = ({
  user,
  productName,
  status,
}: Props & { status: OnboardingState }) => {
  if (TAX_REFUND_ADVANCE_COMPLETED_STATES.includes(status))
    return Progress.complete;

  if (hasInProgressProduct({ user, productName })) return Progress.inProgress;

  return Progress.ready;
};

const ProgressIcon: React.FC<{ progress: Progress }> = ({ progress }) => {
  switch (progress) {
    case Progress.complete:
      return <Icon name="checkCircle" />;
    case Progress.inProgress:
      return <Icon name="spinnerDots" />;
    case Progress.ready:
    default:
      return null;
  }
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OnboardingStateWithProgress: React.FC<Props> = ({
  user,
  productName,
}) => {
  if (!user || !productName) return null;

  const productKey =
    Object.keys(ProductName)[Object.values(ProductName).indexOf(productName)];
  const status = user.onboardingStates?.[productKey];
  const progress = getProgress({ user, productName, status });

  return (
    <Wrapper>
      <OnboardingStatusTag status={status} />

      <ProgressIcon progress={progress} />
    </Wrapper>
  );
};

export default OnboardingStateWithProgress;
